import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import { useUser } from '../contexts/UserContext';
import Sidebar from './Sidebar';
import Loading from './Loading';
import { PremiumFeature } from './PremiumFeature';
import { UserProfile } from '../types/user';

interface Puzzle {
  id: number;
  title: string;
  description: string;
  rules: string[];
  solution: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
}

const puzzles: Puzzle[] = [
  {
    id: 1,
    title: "The Logical Sequence",
    description: "Find the next number in the sequence: 2, 3, 5, 9, 17, ?",
    rules: [
      "Each number is related to the previous number",
      "The pattern involves a mathematical operation",
      "The operation changes in a predictable way"
    ],
    solution: "33",
    difficulty: "Easy"
  },
  {
    id: 2,
    title: "The Binary Pattern",
    description: "Complete the binary pattern: 101, 110, 1001, 1010, ?",
    rules: [
      "The pattern alternates between two different operations",
      "Each number is a valid binary number",
      "The sequence follows a mathematical rule"
    ],
    solution: "1100",
    difficulty: "Medium"
  },
  {
    id: 3,
    title: "The Logical Operators",
    description: "If A = true, B = false, C = true, what is the result of: (A AND B) OR (NOT C AND A)?",
    rules: [
      "Use standard logical operators",
      "Evaluate from left to right",
      "Consider operator precedence"
    ],
    solution: "false",
    difficulty: "Hard"
  }
];

export function Games() {
  const { profile, loading } = useUser();
  const [currentPuzzle, setCurrentPuzzle] = useState<Puzzle | null>(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [score, setScore] = useState(0);
  const [solvedPuzzles, setSolvedPuzzles] = useState<number[]>([]);

  const defaultUser: UserProfile = {
    id: '',
    name: '',
    email: '',
    role: '',
    avatar_url: '',
    is_premium: false
  };

  const handleStartPuzzle = (puzzle: Puzzle) => {
    if (!profile?.is_premium) return;
    setCurrentPuzzle(puzzle);
    setUserAnswer('');
    setFeedback(null);
  };

  const handleSubmitAnswer = () => {
    if (!currentPuzzle) return;

    const isCorrect = userAnswer.toLowerCase().trim() === currentPuzzle.solution.toLowerCase();
    if (isCorrect) {
      setFeedback({ type: 'success', message: 'Correct! Well done!' });
      setScore(prev => prev + (currentPuzzle.difficulty === 'Easy' ? 1 : currentPuzzle.difficulty === 'Medium' ? 2 : 3));
      setSolvedPuzzles(prev => [...prev, currentPuzzle.id]);
    } else {
      setFeedback({ type: 'error', message: 'Incorrect. Try again!' });
    }
  };

  if (loading) return <Loading />;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Sidebar user={profile || defaultUser} />
      <Box sx={{ flexGrow: 1, p: 4 }}>
        <Typography variant="h4" fontWeight={700} mb={3} color="text.primary">
          Logic Puzzle Solver
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Challenge your logical thinking with these carefully crafted puzzles. Each puzzle tests different aspects of logical reasoning.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <Card sx={{ 
            p: 2, 
            borderRadius: 2, 
            bgcolor: 'primary.main', 
            color: 'white',
            boxShadow: 3
          }}>
            <Typography variant="h6">Score</Typography>
            <Typography variant="h4">{score}</Typography>
          </Card>
          <Card sx={{ 
            p: 2, 
            borderRadius: 2, 
            bgcolor: 'success.main', 
            color: 'white',
            boxShadow: 3
          }}>
            <Typography variant="h6">Solved</Typography>
            <Typography variant="h4">{solvedPuzzles.length}</Typography>
          </Card>
        </Box>

        {currentPuzzle ? (
          <Card sx={{ 
            mb: 4, 
            borderRadius: 3,
            boxShadow: 3,
            bgcolor: 'background.paper'
          }}>
            <CardContent>
              <Typography variant="h5" mb={2} color="text.primary">{currentPuzzle.title}</Typography>
              <Typography variant="body1" mb={2} color="text.secondary">{currentPuzzle.description}</Typography>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">Rules:</Typography>
                <ul>
                  {currentPuzzle.rules.map((rule, index) => (
                    <li key={index}>
                      <Typography variant="body2" color="text.secondary">{rule}</Typography>
                    </li>
                  ))}
                </ul>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label="Your Answer"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSubmitAnswer()}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
                <Button 
                  variant="contained" 
                  onClick={handleSubmitAnswer}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    fontWeight: 600
                  }}
                >
                  Submit
                </Button>
              </Box>
              {feedback && (
                <Alert 
                  severity={feedback.type} 
                  sx={{ 
                    mt: 2,
                    borderRadius: 2
                  }}
                >
                  {feedback.message}
                </Alert>
              )}
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
            gap: 3 
          }}>
            {puzzles.map((puzzle) => (
              <PremiumFeature
                key={puzzle.id}
                featureName={`Puzzle ${puzzle.id}`}
                disabled={!profile?.is_premium}
              >
                <Card 
                  sx={{ 
                    borderRadius: 3,
                    cursor: profile?.is_premium ? 'pointer' : 'not-allowed',
                    opacity: solvedPuzzles.includes(puzzle.id) ? 0.7 : 1,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    boxShadow: 3,
                    bgcolor: 'background.paper',
                    '&:hover': profile?.is_premium ? { 
                      transform: 'translateY(-4px)', 
                      boxShadow: 6 
                    } : {}
                  }}
                  onClick={() => handleStartPuzzle(puzzle)}
                >
                  <CardContent>
                    <Typography variant="h6" mb={1} color="text.primary">{puzzle.title}</Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {puzzle.description}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center' 
                    }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          px: 1.5, 
                          py: 0.5, 
                          borderRadius: 1,
                          bgcolor: puzzle.difficulty === 'Easy' ? 'success.light' : 
                                  puzzle.difficulty === 'Medium' ? 'warning.light' : 'error.light',
                          color: 'white',
                          fontWeight: 600
                        }}
                      >
                        {puzzle.difficulty}
                      </Typography>
                      {solvedPuzzles.includes(puzzle.id) && (
                        <Typography 
                          variant="caption" 
                          color="success.main"
                          sx={{ fontWeight: 600 }}
                        >
                          ✓ Solved
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </PremiumFeature>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
} 